import { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';

/*
 * Prop firm presets for the risk tools. The server builds them from the
 * verified rules (re-checked weekly, changes approved by an admin); this
 * copy is only used when the server can't be reached.
 */
const s = (label, drawdown, dailyLossLimit, profitTarget, maxContracts) => ({
  label, accountSize: Number(label.replace('K', '')) * 1000, drawdown, dailyLossLimit, profitTarget, maxContracts,
});

export const FALLBACK_PRESETS = [
  {
    key: 'apex', firm: 'Apex Trader Funding', verifiedAt: '2026-10-18', source: 'https://support.apextraderfunding.com',
    plans: [
      { id: 'apex-intraday', evaluation: { consistency: null, minDays: null, trailing: 'intraday', lockOffset: 100, dailyLoss: null }, plan: 'Intraday evaluation', drawdownType: 'Intraday trailing', consistency: 50, consistencyNote: '50% on funded (PA) payouts; none in the evaluation',
        sizes: [s('25K', 1000, null, 1500, 4), s('50K', 2000, null, 3000, 6), s('100K', 3000, null, 6000, 8), s('150K', 4000, null, 9000, 12)] },
      { id: 'apex-eod', evaluation: { consistency: null, minDays: null, trailing: 'eod', lockOffset: 100, dailyLoss: 'pause' }, plan: 'EOD evaluation', drawdownType: 'End-of-day', consistency: 50, consistencyNote: '50% on funded (PA) payouts; none in the evaluation',
        sizes: [s('25K', 1000, 500, 1500, 4), s('50K', 2000, 1000, 3000, 6), s('100K', 3000, 1500, 6000, 8), s('150K', 4000, 2000, 9000, 12)] },
    ],
  },
  {
    key: 'topstep', firm: 'Topstep', verifiedAt: '2026-10-18', source: 'https://help.topstep.com',
    plans: [
      { id: 'topstep-combine', evaluation: { consistency: 55, minDays: null, trailing: 'eod', lockOffset: 0, dailyLoss: 'pause' }, plan: 'Trading Combine', drawdownType: 'EOD trailing, enforced in real time', consistency: 55, consistencyNote: 'Best day under 55% of the profit target (Combine)',
        sizes: [s('50K', 2000, 1000, 3000, 5), s('100K', 3000, 2000, 6000, 10), s('150K', 4500, 3000, 9000, 15)] },
    ],
  },
  {
    key: 'bulenox', firm: 'Bulenox', verifiedAt: '2026-10-18', source: 'https://bulenox.com',
    plans: [
      { id: 'bulenox-trailing', evaluation: { consistency: null, minDays: null, trailing: 'intraday', lockOffset: 0, dailyLoss: null }, plan: 'Option 1 · Trailing', drawdownType: 'Intraday trailing', consistency: 40, consistencyNote: '40% on Master payouts; none in Qualification',
        sizes: [s('25K', 1500, null, 1500, 3), s('50K', 2500, null, 3000, 7), s('100K', 3000, null, 6000, 12), s('150K', 4500, null, 9000, 15)] },
      { id: 'bulenox-eod', evaluation: { consistency: null, minDays: null, trailing: 'eod', lockOffset: 0, dailyLoss: 'pause' }, plan: 'Option 2 · End-of-day', drawdownType: 'End-of-day', consistency: 40, consistencyNote: '40% on Master payouts; none in Qualification',
        sizes: [s('25K', 1500, 500, 1500, 3), s('50K', 2500, 1100, 3000, 7), s('100K', 3000, 2200, 6000, 12), s('150K', 4500, 3300, 9000, 15)] },
    ],
  },
  {
    key: 'takeprofittrader', firm: 'Take Profit Trader', verifiedAt: '2026-10-18', source: 'https://takeprofittrader.com',
    plans: [
      { id: 'tpt-test', evaluation: { consistency: 50, minDays: 3, trailing: 'eod', lockOffset: 0, dailyLoss: null }, plan: 'Test', drawdownType: 'End-of-day trailing', consistency: 50, consistencyNote: '50% in the Test; none on PRO',
        sizes: [s('25K', 1500, null, 1500, 3), s('50K', 2000, null, 3000, 6), s('75K', 2500, null, 4500, 9), s('100K', 3000, null, 6000, 12), s('150K', 4500, null, 9000, 15)] },
    ],
  },
  {
    key: 'myfundedfutures', firm: 'MyFundedFutures', verifiedAt: '2026-10-18', source: 'https://myfundedfutures.com',
    plans: [
      { id: 'mffu-rapid', evaluation: { consistency: 50, minDays: 2, trailing: 'eod', lockOffset: 0, dailyLoss: null }, plan: 'Rapid', drawdownType: 'EOD (eval) · Intraday (funded)', consistency: 50, consistencyNote: '50% in the evaluation; none when funded', sizes: [s('50K', 2000, null, 3000, null)] },
      { id: 'mffu-rapid-eod', evaluation: { consistency: 30, minDays: 4, trailing: 'eod', lockOffset: 0, dailyLoss: null }, plan: 'Rapid EOD', drawdownType: 'End-of-day trailing', consistency: 30, consistencyNote: '30% in the evaluation; none when funded', sizes: [s('50K', 2000, null, 3000, null)] },
      { id: 'mffu-pro', evaluation: { consistency: 50, minDays: 2, trailing: 'eod', lockOffset: 0, dailyLoss: null }, plan: 'Pro', drawdownType: 'End-of-day trailing', consistency: 50, consistencyNote: '50% in the evaluation; none when funded', sizes: [s('50K', 2000, null, 3000, null)] },
      { id: 'mffu-builder', evaluation: { consistency: null, minDays: 1, trailing: 'eod', lockOffset: 0, dailyLoss: null }, plan: 'Builder', drawdownType: 'End-of-day trailing', consistency: 50, consistencyNote: 'None in the evaluation; 50% per payout cycle when funded', sizes: [s('50K', 2000, null, 3000, null)] },
    ],
  },
];

let cache = null;
let inflight = null;

export function loadPropFirmPresets() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch(API_ENDPOINTS.propFirmPresets)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        cache = d?.success && Array.isArray(d.firms) && d.firms.length
          ? { firms: d.firms, live: true, notListed: Array.isArray(d.notListed) ? d.notListed : [] }
          : { firms: FALLBACK_PRESETS, live: false, notListed: [] };
        return cache;
      })
      .catch(() => ({ firms: FALLBACK_PRESETS, live: false, notListed: [] }))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** { firms, live, loading, notListed } — starts with the built-in copy so the UI never waits. notListed: firms not offered yet, and why. */
export function usePropFirmPresets() {
  const [state, setState] = useState(() => cache ? { ...cache, loading: false } : { firms: FALLBACK_PRESETS, live: false, loading: true, notListed: [] });
  useEffect(() => {
    let alive = true;
    loadPropFirmPresets().then((r) => { if (alive) setState({ ...r, loading: false }); });
    return () => { alive = false; };
  }, []);
  return state;
}

export function findPlan(firms, planId) {
  for (const f of firms || []) {
    const p = (f.plans || []).find((x) => x.id === planId);
    if (p) return { firm: f, plan: p };
  }
  return null;
}

export const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);

export function formatVerified(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const STORE = 'risklo.propFirmPreset';
export function rememberPreset(id) { try { if (id) localStorage.setItem(STORE, id); else localStorage.removeItem(STORE); } catch (e) { /* storage blocked */ } }
export function rememberedPreset() { try { return localStorage.getItem(STORE) || ''; } catch (e) { return ''; } }
